import { Button, Dialog, Flex, Text, toaster, TrashIcon } from 'components'
import { useCurrentUser, useWorkspace } from 'components/providers'
import { cloneDeep, isEmpty, isEqual } from 'lodash-es'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { TemplateRoute, TemplateRouting, TraitWrapper } from 'types'
import { createDoc, removeEmpty, updateDoc } from 'utils'
import { TemplateRouteEditor, TemplateTraitsViewer } from '.'
import { useFullTraitMap } from '../../../utils'
import { getRoutings, makeEmptyTrait } from '../../../utils/traitUtils'
import ManageTraitDrawer from '../../traitContent/ManageTraitDrawer'
import { useTemplate } from '../TemplateProvider'
import NewRoute from './NewRoute'

export interface ManageTemplateTraitsHandle {
  openNewRoute: () => void
}

interface Props {
  onManageLayout: (layoutKey: number) => void
}

const ManageTemplateTraits = forwardRef<ManageTemplateTraitsHandle, Props>(
  function ManageTemplateTraits({ onManageLayout }, ref) {
    const user = useCurrentUser()
    const { workspace } = useWorkspace()
    const { template, updateTemplate } = useTemplate()
    const traitMap = useFullTraitMap()
    const [dialogInitialRouting, setDialogInitialRouting] = useState<TemplateRouting | undefined>()
    const [hoveringRoutes, setHoveringRoutes] = useState<TemplateRoute[]>([])
    const [initialSelectedRoute, setInitialSelectedRoute] = useState<TemplateRoute>()
    const [leafPresetValueIds, setLeafPresetValueIds] = useState<string[] | undefined>()
    const [selectedRoute, setSelectedRoute] = useState<TemplateRoute>()
    const [selectedTraitKey, setSelectedTraitKey] = useState<number>()
    const dialogBodyPortalRef = useRef<HTMLDivElement>(null)
    const openNewRouteRef = useRef(() => {})

    function closeConfigurationDialog() {
      setSelectedRoute(undefined)
      setInitialSelectedRoute(undefined)
      setDialogInitialRouting(undefined)
      setLeafPresetValueIds(undefined)
    }

    function openConfigurationDialog(
      route: TemplateRoute,
      options?: { initialSelectedValueIds?: string[] }
    ) {
      setInitialSelectedRoute(cloneDeep(route))
      setDialogInitialRouting(cloneDeep(template?.routing) ?? undefined)
      setLeafPresetValueIds(options?.initialSelectedValueIds)
      setSelectedRoute(route)
    }

    openNewRouteRef.current = () => openConfigurationDialog([])

    useImperativeHandle(ref, () => ({
      openNewRoute: () => openNewRouteRef.current(),
    }))

    const isDialogOpen = selectedRoute !== undefined
    const isNewRoute = initialSelectedRoute?.length === 0
    const isDirty =
      isDialogOpen &&
      initialSelectedRoute !== undefined &&
      (!isEqual(selectedRoute, initialSelectedRoute) ||
        !isEqual(template?.routing, dialogInitialRouting))
    const selectedTrait = selectedTraitKey == null ? undefined : traitMap?.[selectedTraitKey]

    function handleDone() {
      closeConfigurationDialog()
    }

    function handleRemoveRoute() {
      if (!selectedRoute || !template?.routing) return

      const routing = { ...template.routing }
      const routeStep = selectedRoute[0]
      if (routeStep === 'default') {
        delete routing.default
      } else {
        routing.valueRoutes = routing.valueRoutes?.filter((_, i) => i !== routeStep)
        if (routing.valueRoutes?.length === 0) delete routing.valueRoutes
      }

      updateTemplate({ routing })
      closeConfigurationDialog()
    }

    function handleSecondaryAction() {
      if (!isDialogOpen || initialSelectedRoute === undefined) return

      if (isNewRoute) {
        if (dialogInitialRouting !== undefined) {
          updateTemplate({ routing: cloneDeep(dialogInitialRouting) })
        }
        closeConfigurationDialog()
        return
      }

      if (isDirty) {
        setSelectedRoute(cloneDeep(initialSelectedRoute))
        if (dialogInitialRouting !== undefined) {
          updateTemplate({ routing: cloneDeep(dialogInitialRouting) })
        }
        return
      }

      handleRemoveRoute()
    }

    async function handleTraitDone(body?: TraitWrapper) {
      if (!template || selectedTraitKey == null || !body) return setSelectedTraitKey(undefined)

      const trait = template.traitMap?.[selectedTraitKey]
      if (!trait || isEqual(trait, body)) {
        setSelectedTraitKey(undefined)
        return
      }

      try {
        const newTraitMap = { ...template.traitMap, [selectedTraitKey]: { ...trait, ...body } }
        updateTemplate({ traitMap: newTraitMap })
      } catch (error) {
        console.error(error)
        toaster.create({ type: 'error', description: 'Failed to update trait' })
      }

      setSelectedTraitKey(undefined)
    }

    async function handleTraitDoneAll(body?: TraitWrapper) {
      if (!template || !workspace || selectedTraitKey == null) return
      if (!body) return setSelectedTraitKey(undefined)

      const { id } = body

      try {
        let nextId = id
        if (id) {
          await updateDoc(user!.id, 'traits2', { id, ...removeEmpty(body) })
        } else {
          const doc = { ...makeEmptyTrait(workspace.id), ...body }
          const created = await createDoc(user!.id, 'traits2', removeEmpty(doc))
          nextId = created.id
        }

        const newTraitMap = { ...template.traitMap, [selectedTraitKey]: { id: nextId } }
        updateTemplate({ traitMap: newTraitMap })
      } catch (error) {
        console.error(error)
        toaster.create({ type: 'error', description: 'Failed to save trait' })
      }

      setSelectedTraitKey(undefined)
    }

    function handleTraitReset() {
      if (!template || !traitMap || selectedTraitKey == null) return

      const trait = traitMap[selectedTraitKey]
      const newTraitMap = { ...template.traitMap, [selectedTraitKey]: { id: trait.id } }
      updateTemplate({ traitMap: newTraitMap })
    }

    return (
      <>
        {isEmpty(template?.routing) ? (
          <Flex direction="column" flex={1} gap={6} minHeight={0} overflow="auto">
            <Flex direction="column" gap={2}>
              <Text textStyle="h6">Choose the entry point</Text>
              <Text color="gray.700" fontSize="sm">
                Start routing by selecting or creating an initial trait, or choose or create a
                layout.
              </Text>
            </Flex>

            <NewRoute
              onEditTrait={setSelectedTraitKey}
              onUpdate={() => {}}
              route={[]}
              routings={getRoutings(template?.routing, [])}
              selectedIds={[]}
              traitMap={traitMap}
            />
          </Flex>
        ) : (
          <TemplateTraitsViewer
            hoveringRoutes={hoveringRoutes}
            onEditTrait={setSelectedTraitKey}
            onHoverRoutes={setHoveringRoutes}
            onManageLayout={onManageLayout}
            onSelectRoute={openConfigurationDialog}
          />
        )}

        <Dialog
          bodyProps={{ display: 'flex', overflowX: 'auto', overflowY: 'hidden', px: 8, pt: 4 }}
          open={isDialogOpen}
          size="cover"
          hideClose
          title={
            <Flex alignItems="center" gap={2} width="100%" justifyContent="flex-end">
              <Text>Edit Configuration</Text>
              <Flex gap={2} marginLeft="auto">
                <Button
                  leftIcon={!isNewRoute && !isDirty ? <TrashIcon /> : undefined}
                  onClick={handleSecondaryAction}
                  palette={!isNewRoute && !isDirty ? 'danger' : undefined}
                  variant="ghost"
                >
                  {isNewRoute ? 'Cancel' : isDirty ? 'Reset' : 'Remove'}
                </Button>
                <Button onClick={handleDone} palette="primary">
                  Done
                </Button>
              </Flex>
            </Flex>
          }
        >
          <Flex ref={dialogBodyPortalRef}>
            <TemplateRouteEditor
              initialSelectedValueIds={leafPresetValueIds}
              onEditTrait={setSelectedTraitKey}
              onUpdate={setSelectedRoute}
              route={selectedRoute}
            />
          </Flex>
        </Dialog>

        <ManageTraitDrawer
          key={isDialogOpen ? `configuration-dialog` : 'trait-routing'}
          onDone={handleTraitDone}
          onDoneAll={handleTraitDoneAll}
          onReset={selectedTrait?.id && selectedTrait?.hasEdits ? handleTraitReset : undefined}
          open={selectedTraitKey != null}
          portalProps={isDialogOpen ? { container: dialogBodyPortalRef } : undefined}
          trait={selectedTrait}
        />
      </>
    )
  }
)

export default ManageTemplateTraits
